/**
 * @userstory US-FM-03, US-FM-07
 *
 * Company route guard for Freelance Management surfaces.
 * Never-active organizations are redirected to subscription activation.
 * Historical organizations may enter read-only archive views.
 */
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EntitlementKeys } from '../../common/constants/entitlementKeys';
import { useOrganization } from '../../contexts/organizationContext';
import { useEntitlements } from '../../hooks/useEntitlements';
import entitlementService from '../../services/api/entitlementService';
import Alert from '../bootstrap/Alert';

interface CompanyFreelanceManagementAddOnGuardProps {
	children: ReactNode;
}

/** @userstory US-FM-03, US-FM-07 */
const CompanyFreelanceManagementAddOnGuard: FC<CompanyFreelanceManagementAddOnGuardProps> = ({
	children,
}) => {
	const { selectedOrganization } = useOrganization();
	const { hasAddOn } = useEntitlements();
	const { t } = useTranslation('entitlements');
	const [canEnter, setCanEnter] = useState<boolean | null>(null);
	const [isReadOnlyArchive, setIsReadOnlyArchive] = useState(false);

	useEffect(() => {
		let isMounted = true;

		const loadState = async () => {
			if (!selectedOrganization?.id) {
				if (isMounted) setCanEnter(null);
				return;
			}

			if (hasAddOn(EntitlementKeys.AddOns.FreelanceManagement)) {
				if (isMounted) {
					setCanEnter(true);
					setIsReadOnlyArchive(false);
				}
				return;
			}

			try {
				const state = await entitlementService.getAddonState(
					selectedOrganization.id,
					EntitlementKeys.AddOns.FreelanceManagement,
				);
				if (isMounted) {
					setCanEnter(state.active || state.everActive === true);
					setIsReadOnlyArchive(!state.active && state.everActive === true);
				}
			} catch {
				if (isMounted) {
					setCanEnter(false);
					setIsReadOnlyArchive(false);
				}
			}
		};

		loadState();

		return () => {
			isMounted = false;
		};
	}, [hasAddOn, selectedOrganization?.id]);

	if (canEnter === null) return null;

	if (!canEnter) {
		return (
			<Navigate
				to='/company/settings/subscription?addon=freelance_management'
				replace
			/>
		);
	}

	return (
		<>
			{isReadOnlyArchive && (
				<Alert color='warning' isLight icon='Lock' className='mb-4'>
					{t('addon.freelance_management.readOnlyBanner')}
				</Alert>
			)}
			{children}
		</>
	);
};

export default CompanyFreelanceManagementAddOnGuard;
